import { useEffect, useRef, useState } from 'react'
import type { Verdict } from '@/types'

// Allineati a design-tokens-italia v1.3.3 (seagreen/orange/red)
const VERDICT_HEX: Record<Verdict, string> = {
  VERDE: '#007A52',
  GIALLO: '#CC7A00',
  ROSSO: '#CC334D',
  VIETATO: '#7A1F2E',
}

const R = 54
const CIRC = Math.PI * R // semicircle length

/**
 * Animated semicircular score gauge (0-5). The arc draws itself on mount
 * and the number counts up in sync.
 */
export function ScoreGauge({ score, verdict }: { score: number; verdict: Verdict }) {
  const [mounted, setMounted] = useState(false)
  const [display, setDisplay] = useState(0)
  const raf = useRef<number>()

  const clamped = Math.max(0, Math.min(5, score))
  const target = (clamped / 5) * CIRC
  const color = VERDICT_HEX[verdict] ?? '#9CA3AF'

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    const start = performance.now()
    const duration = 1100
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setDisplay(clamped * eased)
      if (p < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(id)
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [clamped])

  return (
    <div className="relative w-36 h-20 flex-shrink-0" role="img" aria-label={`Score ${clamped.toFixed(1)} su 5`}>
      <svg viewBox="0 0 128 70" className="w-full h-full overflow-visible">
        {/* Track — il gauge vive sul banner del verdetto, che ha uno sfondo
            chiaro fisso anche in dark mode: la traccia resta scura, non segue
            i token del tema (in dark diventerebbe quasi invisibile) */}
        <path
          d={`M 10 64 A ${R} ${R} 0 0 1 118 64`}
          fill="none"
          stroke="currentColor"
          strokeWidth="9"
          strokeLinecap="round"
          className="text-black/10"
        />
        {/* Value arc */}
        <path
          d={`M 10 64 A ${R} ${R} 0 0 1 118 64`}
          fill="none"
          stroke={color}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={mounted ? CIRC - target : CIRC}
          style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center leading-none">
        <span className="text-2xl font-bold tabular-nums" style={{ color }}>
          {display.toFixed(1)}
        </span>
        <span className="text-xs text-black/60 font-medium">/5</span>
      </div>
    </div>
  )
}
